import { useId, type ReactNode } from "react";
import type { IndicadorDeFilas, VisaoGeral } from "@/api/contrato";
import { PilulaDeSituacao } from "@/componentes/PilulaDeSituacao";
import { quantidade } from "@/formato";

/*
 * A fita de números da visão geral: fila morta, vazão e p95 por etapa (design D6).
 *
 * Fita de números, e não gráfico: a API devolve agregado da janela, sem série por minuto, e
 * um gráfico exigiria rota e dependência novas sem responder pergunta nova.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ UMA LINHA, COM A ROLAGEM NELA.                                           │
 * │                                                                          │
 * │ A fita não quebra (doc 06a §4.2): o que não couber rola dentro dela. Por │
 * │ isso a fila morta vem primeiro, na ordem do doc 06 §4.1, e a superfície  │
 * │ recebe foco: rolagem que só o mouse alcança esconde número do teclado.   │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function FitaDeOperacao({ visao }: { visao: VisaoGeral }) {
  const janela = textoDaJanela(visao.janela_segundos);

  return (
    <section
      aria-label="Fita de operação"
      tabIndex={0}
      className="flex divide-x divide-fio overflow-x-auto rounded-cartao border border-fio bg-superficie px-4 py-2"
    >
      <Trecho rotulo="Fila morta">
        <FilaMorta filas={visao.filas} />
      </Trecho>
      <Trecho rotulo={`Vazão · ${janela}`}>
        <Vazao vazao={visao.vazao} />
      </Trecho>
      <Trecho rotulo={`Latência p95 · ${janela}`}>
        <Latencia latencia={visao.latencia} />
      </Trecho>
    </section>
  );
}

/** Quantas mensagens o broker contou nas filas mortas de todos os fluxos. */
export function mensagensNaFilaMorta(filas: IndicadorDeFilas): number {
  return (filas.fluxos ?? []).reduce((soma, fluxo) => soma + fluxo.morta, 0);
}

/*
 * Fila morta que o broker não tem conta zero com o indicador em `ok`: é broker recriado sem
 * topologia, e o zero ali não é fila vazia. Fila de trabalho ausente não entra aqui, porque
 * não põe a contagem da morta em dúvida.
 */
export function filaMortaSemContagem(filas: IndicadorDeFilas): boolean {
  return filas.fluxos?.some((fluxo) => fluxo.ausentes.includes(fluxo.fila_morta)) === true;
}

/*
 * Zero e "não sei" são fatos diferentes. Nunca mostre zero para uma fonte que não
 * respondeu: seria dizer "está tudo bem" no momento em que não está. E "não configurado"
 * e "indisponível" levam a ações opostas, uma a um Secret, outra a quem cuida do serviço.
 */
export function textoDoEstado(estado: string | undefined): string {
  if (estado === "nao_configurado") return "não configurado";
  if (estado === "indisponivel") return "não foi possível medir";
  return "—";
}

function Trecho({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  const id = useId();

  return (
    <div role="group" aria-labelledby={id} className="shrink-0 px-4 first:pl-0 last:pr-0">
      <div id={id} className="rotulo whitespace-nowrap">
        {rotulo}
      </div>
      {/* Altura fixa: número, pílula e texto de estado se alternam sem a fita pular. */}
      <div className="mt-[2px] flex h-6 items-center whitespace-nowrap text-[12px]">{children}</div>
    </div>
  );
}

function SemMedida({ estado }: { estado: string | undefined }) {
  return <span className="text-texto-2">{textoDoEstado(estado)}</span>;
}

function FilaMorta({ filas }: { filas: VisaoGeral["filas"] }) {
  if (filas?.estado !== "ok") return <SemMedida estado={filas?.estado} />;
  if (filaMortaSemContagem(filas)) return <SemMedida estado="indisponivel" />;

  const total = mensagensNaFilaMorta(filas);
  return (
    <span className={`dados text-corpo ${total > 0 ? "text-falha" : "text-texto"}`}>
      {quantidade(total)}
    </span>
  );
}

/*
 * A ordem do ciclo, e não a da resposta: a API ordena por contagem, e a fita trocaria as
 * pílulas de lugar a cada dez segundos. Situação nova, que a tela ainda não conhece, entra
 * no fim, com o texto cru.
 */
const ORDEM_DO_CICLO = [
  "RECEBIDO",
  "ENFILEIRADO",
  "ENVIADO",
  "EMITIDO",
  "REGISTRADO",
  "LIQUIDADO",
  "FALHA",
  "REJEITADO",
  "PROTESTADO",
  "BAIXADO",
  "DESCARTADO",
  "CANCELADO",
];

function posicaoNoCiclo(situacao: string): number {
  const posicao = ORDEM_DO_CICLO.indexOf(situacao);
  return posicao === -1 ? ORDEM_DO_CICLO.length : posicao;
}

function Vazao({ vazao }: { vazao: VisaoGeral["vazao"] }) {
  if (vazao?.estado !== "ok") return <SemMedida estado={vazao?.estado} />;

  const desfechos = [...(vazao.desfechos ?? [])].sort(
    (a, b) =>
      posicaoNoCiclo(a.situacao) - posicaoNoCiclo(b.situacao) ||
      a.situacao.localeCompare(b.situacao),
  );
  if (desfechos.length === 0) return <span className="text-texto-2">sem movimento</span>;

  return (
    <dl className="flex items-center gap-3">
      {desfechos.map(({ situacao, total }) => (
        <div key={situacao} className="flex items-center gap-1">
          <dt>
            <PilulaDeSituacao situacao={situacao} />
          </dt>
          <dd className="dados text-texto">{quantidade(total)}</dd>
        </div>
      ))}
    </dl>
  );
}

const ETAPAS: Record<string, string> = {
  recebido_para_enviado: "Recebido → enviado",
  enviado_para_emitido: "Enviado → emitido",
  emitido_para_registrado: "Emitido → registrado",
  registrado_para_liquidado: "Registrado → liquidado",
};

function Latencia({ latencia }: { latencia: VisaoGeral["latencia"] }) {
  if (latencia?.estado !== "ok") return <SemMedida estado={latencia?.estado} />;

  return (
    <dl className="flex items-center gap-4">
      {latencia.etapas?.map(({ etapa, p95_ms }) => (
        <div key={etapa} className="flex items-baseline gap-1">
          <dt className="text-texto-2">{ETAPAS[etapa] ?? etapa}</dt>
          {/* Sem `p95_ms` é sem amostra: etapa que ninguém percorreu não tem latência zero. */}
          {p95_ms === undefined ? (
            <dd className="text-texto-2">sem amostra</dd>
          ) : (
            <dd className="dados text-texto">{duracao(p95_ms)}</dd>
          )}
        </div>
      ))}
    </dl>
  );
}

function decimal(valor: number): string {
  return valor.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

/** `180 ms`, `2,4 s`, `2,5 min`, `1,5 h`: registro no banco leva horas, e envio, milissegundos. */
function duracao(milissegundos: number): string {
  if (milissegundos < 1000) return `${milissegundos} ms`;

  const segundos = milissegundos / 1000;
  if (segundos < 60) return `${decimal(segundos)} s`;

  const minutos = segundos / 60;
  if (minutos < 60) return `${decimal(minutos)} min`;

  return `${decimal(minutos / 60)} h`;
}

function textoDaJanela(segundos: number): string {
  if (segundos % 3600 === 0) return `${segundos / 3600} h`;
  if (segundos % 60 === 0) return `${segundos / 60} min`;
  return `${segundos} s`;
}
